/**
 * Provides pure guided-experience state transitions. Helpers accept state or
 * configuration inputs, return immutable state values, and have no side effects.
 *
 * @module marketing/state/experienceState
 */

/**
 * Identifies a ready experience item. This constant takes no inputs, is the
 * exported phase value, and has no side effects.
 */
export const IDLE = 'idle';

/**
 * Identifies an experience item awaiting advancement. This constant takes no
 * inputs, is the exported phase value, and has no side effects.
 */
export const TRANSITIONING = 'transitioning';

/**
 * Identifies a finished experience. This constant takes no inputs, is the
 * exported phase value, and has no side effects.
 */
export const COMPLETE = 'complete';

/**
 * Creates the initial immutable state for a finite guided experience.
 *
 * @param {number} total - Positive integer count of experience items.
 * @returns {{index: number, total: number, phase: string, selectedValue: null}}
 *   A new initial experience state object.
 * @sideEffects None.
 */
export function createExperienceState(total) {
  if (!Number.isInteger(total) || total < 1) {
    throw new Error('Experience total must be a positive integer.');
  }

  return {
    index: 0,
    total,
    phase: IDLE,
    selectedValue: null,
  };
}

/**
 * Records a selection when the experience is idle and begins its transition.
 *
 * @param {{phase: string}} state - Current experience state.
 * @param {*} selectedValue - Non-null option value to record.
 * @returns {object} A new transitioning state, or the unchanged input state
 *   when selection is unavailable.
 * @sideEffects None.
 */
export function selectExperienceOption(state, selectedValue) {
  if (
    state.phase !== IDLE
    || selectedValue === null
    || selectedValue === undefined
  ) {
    return state;
  }

  return {
    ...state,
    phase: TRANSITIONING,
    selectedValue,
  };
}

/**
 * Advances a transitioning experience to its next item or completed phase.
 *
 * @param {{index: number, total: number, phase: string}} state - Current
 *   experience state.
 * @returns {object} The next immutable experience state, or the unchanged
 *   input state when it is not transitioning.
 * @sideEffects None.
 */
export function advanceExperience(state) {
  if (state.phase !== TRANSITIONING) {
    return state;
  }

  if (state.index >= state.total - 1) {
    return {
      ...state,
      phase: COMPLETE,
    };
  }

  return {
    ...state,
    index: state.index + 1,
    phase: IDLE,
    selectedValue: null,
  };
}

/**
 * Resets an experience while retaining its configured total item count.
 *
 * @param {{total: number}} state - Experience state whose total is retained.
 * @returns {object} A new initial experience state.
 * @sideEffects None.
 */
export function resetExperience(state) {
  return createExperienceState(state.total);
}
